/**
 * useDataSync — synchronizuje wallets/categories/transactions store ze źródłem:
 *  - Dev mode: ładuje DEV_* mocks (sync, jednorazowo)
 *  - Prod: Firebase onSnapshot listenery na collections filtered by familyId.
 *
 * Hook na top level Layout (po AuthGate). Cleanup unsubscribers przy unmount /
 * familyId change.
 */
import { useEffect } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db, COLLECTIONS, IS_DEV_MODE } from '../lib/firebase';
import { useAuthStore } from './auth/authStore';
import { useWalletsStore } from './wallets/walletsStore';
import { useTransactionsStore, type Transaction, type TxType } from './transactions/transactionsStore';
import { useCategoriesStore } from './categories/categoriesStore';
import { DEV_WALLETS, DEV_CATEGORIES, DEV_TRANSACTIONS, DEV_GOALS, DEV_BUDGETS } from '../lib/devBridge';
import { useGoalsStore } from './goals/goalsStore';
import { useBudgetsStore } from './budgets/budgetsStore';

export function useDataSync(): void {
  const family = useAuthStore((s) => s.family);
  const setWallets = useWalletsStore((s) => s.setWallets);
  const setCategories = useCategoriesStore((s) => s.setCategories);
  const setTransactions = useTransactionsStore((s) => s.setTransactions);
  const setLoading = useTransactionsStore((s) => s.setLoading);

  // Wstępna selekcja: zostawiamy 'all' (pseudo-wallet "Wspólny" agregujący
  // wszystkie portfele rodziny — patrz WalletScroller). Wcześniej ten effect
  // nadpisywał 'all' na pierwszy personal wallet, co psuło legacy users
  // którzy nie mają w bazie wallet typu 'shared'.

  useEffect(() => {
    if (!family) return;

    if (IS_DEV_MODE) {
      setWallets(DEV_WALLETS.map((w) => ({ ...w })));
      setCategories(DEV_CATEGORIES.map((c) => ({ ...c })));
      useGoalsStore.getState().setGoals(DEV_GOALS.map((g) => ({ ...g, deposits: [...g.deposits] })));
      useBudgetsStore.getState().setBudgets(DEV_BUDGETS.map((b) => ({ ...b })));
      setTransactions(
        DEV_TRANSACTIONS.map((t) => ({
          id: t.id,
          familyId: t.familyId,
          walletId: t.walletId,
          toWalletId: t.toWalletId,
          userId: t.userId,
          type: t.type as TxType,
          amount: t.amount,
          categoryId: t.categoryId,
          note: t.note,
          date: t.date,
          isRecurring: t.isRecurring,
          isPaused: t.isPaused,
          syncStatus: t.syncStatus,
        })),
      );
      setLoading(false);
      return;
    }

    // Prod: 3 onSnapshot listenery z cleanup
    setLoading(true);
    let txReady = false;
    const onError = (label: string) => (err: Error) => {
      console.error(`[dataSync] ${label} onSnapshot error:`, err);
      setLoading(false);
    };

    const unsubWallets = onSnapshot(
      query(collection(db, COLLECTIONS.wallets), where('familyId', '==', family.id)),
      (snap) => {
        const wallets = snap.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            familyId: d.familyId,
            ownerId: d.ownerId,
            name: d.name,
            type: d.type,
            color: d.color,
          };
        });
        setWallets(wallets);
      },
      onError('wallets'),
    );

    const unsubCategories = onSnapshot(
      query(collection(db, COLLECTIONS.categories), where('familyId', '==', family.id)),
      (snap) => {
        const cats = snap.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            familyId: d.familyId,
            name: d.name,
            icon: d.icon,
            color: d.color,
            type: d.type,
            isDefault: d.isDefault ?? false,
            order: d.order ?? 999,
          };
        });
        setCategories(cats);
      },
      onError('categories'),
    );

    const unsubTx = onSnapshot(
      // Bez orderBy — żeby uniknąć wymogu composite indeksu (familyId + date).
      // Sortujemy client-side po fetched array (tanio, < 1000 tx typowy use-case).
      query(
        collection(db, COLLECTIONS.transactions),
        where('familyId', '==', family.id),
      ),
      (snap) => {
        const txs = snap.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            familyId: d.familyId,
            walletId: d.walletId,
            toWalletId: d.toWalletId,
            userId: d.userId,
            type: d.type as TxType,
            amount: d.amount,
            categoryId: d.categoryId,
            note: d.note ?? '',
            date: d.date?.toDate ? d.date.toDate() : new Date(d.date),
            isRecurring: d.isRecurring ?? false,
            isPaused: d.isPaused,
            goalId: d.goalId,
            syncStatus: 'synced' as const,
          } satisfies Transaction;
        });
        // Sort by date desc client-side (zastępuje orderBy w query)
        txs.sort((a, b) => b.date.getTime() - a.date.getTime());
        setTransactions(txs);
        if (!txReady) {
          txReady = true;
          setLoading(false);
        }
      },
      onError('transactions'),
    );

    const unsubBudgets = onSnapshot(
      query(collection(db, COLLECTIONS.budgets), where('familyId', '==', family.id)),
      (snap) => {
        const budgets = snap.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            familyId: d.familyId,
            walletId: d.walletId ?? 'all',
            categoryId: d.categoryId,
            amount: d.amount,
            period: d.period ?? 'monthly',
            alertAt: d.alertAt ?? 80,
          };
        });
        useBudgetsStore.getState().setBudgets(budgets);
      },
      onError('budgets'),
    );

    const unsubGoals = onSnapshot(
      query(collection(db, COLLECTIONS.goals), where('familyId', '==', family.id)),
      (snap) => {
        const goals = snap.docs.map((doc) => {
          const d = doc.data();
          const deposits = Array.isArray(d.deposits) ? d.deposits : [];
          return {
            id: doc.id,
            familyId: d.familyId,
            name: d.name,
            icon: d.icon,
            color: d.color,
            target: d.target,
            deadline: d.deadline?.toDate ? d.deadline.toDate() : null,
            createdAt: d.createdAt?.toDate ? d.createdAt.toDate() : new Date(),
            deposits: deposits.map((dep: { id: string; amount: number; date: { toDate?: () => Date } | Date; type: 'manual' | 'wallet'; walletId: string | null }) => ({
              id: dep.id,
              amount: dep.amount,
              date: dep.date && typeof dep.date === 'object' && 'toDate' in dep.date && typeof dep.date.toDate === 'function' ? dep.date.toDate() : (dep.date as Date),
              type: dep.type,
              walletId: dep.walletId,
            })),
          };
        });
        useGoalsStore.getState().setGoals(goals);
      },
      onError('goals'),
    );

    return () => {
      unsubWallets();
      unsubCategories();
      unsubTx();
      unsubBudgets();
      unsubGoals();
    };
  }, [family, setWallets, setCategories, setTransactions, setLoading]);
}
